import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Diamond, TrendingUp, Award, Users, Package, BarChart3, Clock, Gift, Star, Trophy, Gem, Sparkles, BadgeDollarSign, Loader2, Instagram, Receipt } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { navigateToRoute } from '@/utils/environment-urls';
import { saleService } from '@/services/sale.service';
import { userService } from '@/services/user.service';
import { EstatisticasMensais } from '@/types/estatisticas-mensais';
import { useToast } from '@/hooks/use-toast';
import { getSubdomainFranchise } from '@/utils/get-subdomain';
import CompletarCadastro from '@/components/CompletarCadastro';

const Home: React.FC = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [estatisticas, setEstatisticas] = useState<EstatisticasMensais | null>(null);
  const [loading, setLoading] = useState(true);
  const [showCompletarCadastro, setShowCompletarCadastro] = useState(false);
  const userData = localStorage.getItem('userlogged');
  const user = userData ? JSON.parse(userData) : null;

  useEffect(() => {
    if(!user || !user?.role){
      location.reload();
      return;
    }
    if(user?.role == 'cliente') {
      navigate('/home-cliente');
      return;
    }    
    
    // Verificação de pré-cadastro para subdomain 'matriz'
    checkPreRegister();
    loadEstatisticas();
  }, []);

  const checkPreRegister = async () => {
    const subdomain = getSubdomainFranchise();
    const token = localStorage.getItem('token');
    
    if (subdomain === 'matriz' && user?.cpf_cnpj) {
      try {
        const response = await userService.checkPreRegister(user.cpf_cnpj);
        const preRegisterData = response.data;
        
        if (preRegisterData && preRegisterData.situacao && 
            (preRegisterData.situacao === 'aguardando_assinaturas' || 
             preRegisterData.situacao === 'verificando_contrato')) {
          // Redireciona para a página de primeiro acesso no React
          navigate(`/primeiro-acesso/${preRegisterData.id}`);
          return;
        } else if (token && user && !user.address) {
          // Se não tem pré-cadastro pendente mas não tem endereço, abre modal
          setShowCompletarCadastro(true);
        }
      } catch (error) {
        console.error('Erro ao verificar pré-cadastro:', error);        
      }
    } else if (token && user && !user.address) {
      // Para outros subdomains, só verifica se tem endereço
      setShowCompletarCadastro(true);
    }
  };

  const handleCompletarCadastroSuccess = (newUserData: any) => {
    localStorage.setItem('userlogged', JSON.stringify(newUserData));
    setShowCompletarCadastro(false);
  };

  const loadEstatisticas = async () => {
    if (!user?.id) {
      setLoading(false);
      return;
    }

    try {
      const data = await saleService.getEstatisticasMensais(user.id);
      setEstatisticas(data);
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
      toast({
        title: "Erro",
        description: "Não foi possível carregar as estatísticas.",
        variant: "destructive",
      });
      // Em caso de erro, mantém valores padrão para não quebrar a interface
      setEstatisticas({
        user_id: user.id,
        nome_usuario: user.nome_razao_social,
        vendas_mes: 0,
        saldo_diamantes: 0,
        posicao_ranking: 0,
        total_vendedores: 0
      });
    } finally {
      setLoading(false);
    }
  };

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL'
    }).format(value);
  };

  const openInstagram = () => {
    window.open('https://www.instagram.com/xdoinfinito/', '_blank');
  };


  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      {/* Header com saudação */}
      <div className="px-4 pt-6 pb-4">
        <div className="flex items-center justify-between mb-2">
          <div>
            <h1 className="text-2xl font-bold text-foreground">
              Oi{user ? ', ' + user.nome_razao_social.split(' ')[0] : ''}!
            </h1>
            <p className="text-muted-foreground text-sm">
              Bem-vinda(o) ao seu portal de vendas
            </p>
          </div>
          <Badge variant="outline" className="text-primary border-primary bg-primary/5">
            {user?.role == 'coordenadora' ? 'Coordenador(a)' : user?.role == 'revendedora' ? 'Revendedor(a)' : user?.role == 'cliente' ? 'Cliente' : user?.role == 'admin' ? 'Admin' : user?.role}
          </Badge>
        </div>
      </div>

      {/* Cards de métricas principais */}
      <div className="px-4 mb-6">
        <div className="grid grid-cols-3 gap-3">
          <Card className="bg-gradient-to-br from-primary/5 to-primary/10 border-primary/20">
            <CardContent className="p-3 text-center">
              <BadgeDollarSign className="w-5 h-5 text-primary mx-auto mb-1" />
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-primary mx-auto mb-1" />
              ) : (
                <div className="text-sm truncate font-bold text-foreground">
                  {formatCurrency(estatisticas?.vendas_mes || 0)}
                </div>
              )}
              <div className="text-xs text-muted-foreground">Vendas no mês</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-gold/5 to-gold/10 border-gold/20">
            <CardContent className="p-3 text-center">
              <Gem className="w-5 h-5 text-gold mx-auto mb-1" />
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-gold mx-auto mb-1" />
              ) : (
                <div className="text-lg font-bold text-gold">{estatisticas?.saldo_diamantes || 0}</div>
              )}
              <div className="text-xs text-muted-foreground">Diamantes</div>
            </CardContent>
          </Card>

          <Card className="bg-gradient-to-br from-purple/5 to-purple/10 border-purple/20">
            <CardContent className="p-3 text-center">
              <Award className="w-5 h-5 text-purple mx-auto mb-1" />
              {loading ? (
                <Loader2 className="w-4 h-4 animate-spin text-purple mx-auto mb-1" />
              ) : (
                <div className="text-lg font-bold text-purple">
                  {estatisticas?.posicao_ranking > 0 ? `${estatisticas.posicao_ranking}º` : `--º`}
                </div>
              )}
              <div className="text-xs text-muted-foreground">Ranking</div>
            </CardContent>
          </Card>
        </div>
      </div>

      {/* Navegação rápida */}
      <div className="px-4 mb-6">
        <div className="grid grid-cols-4 gap-4">
          <button
            onClick={() => navigate('/minha-rede')}
            className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card hover:bg-accent transition-colors"
          >
            <div className="w-12 h-12 bg-primary/10 rounded-xl flex items-center justify-center">
              <Users className="w-6 h-6 text-primary" />
            </div>
            <span className="text-xs font-medium text-foreground">Minha Rede</span>
          </button>

          <button
            onClick={() => navigateToRoute('/minhas-vendas', true)}
            className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card hover:bg-accent transition-colors"
          >
            <div className="w-12 h-12 bg-blue-100 rounded-xl flex items-center justify-center">
              <Receipt className="w-6 h-6 text-blue-600" />
            </div>
            <span className="text-xs font-medium text-foreground">Minhas Vendas</span>
          </button>

          <button
            onClick={() => navigateToRoute('/ranking-vendas', true)}
            className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card hover:bg-accent transition-colors"
          >
            <div className="w-12 h-12 bg-purple/10 rounded-xl flex items-center justify-center">
              <Trophy className="w-6 h-6 text-purple" />
            </div>
            <span className="text-xs font-medium text-foreground">Ranking vendas</span>
          </button>

          <button
            onClick={() => navigateToRoute('/loja-recompensas', true)}
            className="flex flex-col items-center gap-2 p-3 rounded-xl bg-card hover:bg-accent transition-colors"
          >
            <div className="w-12 h-12 bg-gold/10 rounded-xl flex items-center justify-center">
              <Gem className="w-6 h-6 text-gold" />
            </div>
            <span className="text-xs font-medium text-foreground">Loja de Recompensas</span>
          </button>
        </div>
      </div>

      {/* Cards de promoções e benefícios */}
      <div className="px-4 space-y-4">
        {/* Programa de benefícios */}
        <Card className="bg-gradient-to-r from-primary to-primary-hover border-primary/20 overflow-hidden"
        onClick={()=>{
          navigateToRoute('/loja-recompensas', true);
        }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Badge className="bg-white/20 text-white border-white/30 mb-2">
                  Programa de Benefícios
                </Badge>
                <div className="text-white text-sm">
                  Acumule diamantes com suas vendas e indicações e resgate produtos incríveis na Loja de Recompensas.
                </div>
                {/* <button className="text-white/80 text-xs underline mt-1">
                  Saiba mais
                </button> */}
              </div>
              <Gem className="w-8 h-8 text-white/60" />
            </div>
          </CardContent>
        </Card>

        {/* Indique e ganhe */}
        <Card className="bg-gradient-to-r from-secondary to-accent border-secondary/20" 
        onClick={()=>{
          navigateToRoute('/minha-rede', true);
        }}>
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Badge className="bg-primary/10 text-primary border-primary/20 mb-2">
                  Indique e ganhe
                </Badge>
                <div className="text-foreground text-xs">
                  <b>Convide amigos(as) para serem clientes e receba +15 diamantes de bônus na 1ª compra a partir de R$100 de cada indicação.</b><br/><br/>
                  Você também pode indicar para ser Revendedora X. Acumule diamantes em até 3 níveis de todos os seus indicados!
                </div>
                {/* <button className="text-primary text-xs underline mt-1">
                  Ver meu link
                </button> */}
              </div>
              <Gift className="w-8 h-8 text-primary/60" />
            </div>
          </CardContent>
        </Card>

        <Card className="bg-gradient-to-r from-purple/10 to-purple-400" >
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Badge className="bg-purple/10 text-primary border-primary/20 mb-2">
                  Não perca seus benefícios!
                </Badge>
                <div className="text-foreground text-xs">
                  <b>Para garantir seus diamantes acumulados pelas indicações, é preciso realizar ao menos R$500 em vendas no mês.</b><br/><br/>
                  Assim sua conta fica ativa e na virada do mês, os diamantes acumulados na rede, serão creditados no seu saldo da Loja de Recompensas!
                </div>
              </div>
              <Award className="w-8 h-8 text-primary/60" />
            </div>
          </CardContent>
        </Card>

        {/* Novidades */}
        <Card className="bg-gradient-to-r from-gold-light to-gold border-gold-light">
          <CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex-1">
                <Badge className="bg-gold-light text-gold-dark border-gold mb-2">
                  Novidades
                </Badge>
                <h3 className="text-foreground font-semibold text-sm mb-1">
                  Confira os novos produtos!
                </h3>
                <div className="text-foreground text-sm">Fique por dentro dos últimos lançamentos e compartilhe com suas clientes para vender mais!</div>
                <div className="flex items-center justify-between p-3 bg-pink-50 rounded-lg border border-pink-200 mt-4">
                  <div className="flex items-center gap-3">
                    <div className="p-2 bg-gradient-to-br from-purple-500 to-pink-500 rounded-full">
                      <Instagram className="w-4 h-4 text-white" />
                    </div>
                    <div>
                      <p className="font-medium text-pink-800">Instagram</p>
                      <p className="text-sm text-pink-700">@xdoinfinito</p>
                    </div>
                  </div>
                  <Button
                    size="sm"
                    onClick={openInstagram}
                    className="bg-gradient-to-r from-purple-500 to-pink-500 hover:from-purple-600 hover:to-pink-600"
                  >
                    Acessar
                  </Button>
                </div>
              </div>
              <Sparkles className="w-8 h-8 text-gold-dark" />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Seção de novidades */}
      {/* <div className="px-4 py-6">
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-lg font-semibold text-foreground">
            Novidades que combinam com seu estilo de vendas
          </h2>
          <button className="text-primary text-sm">Ver todas</button>
        </div>
        
        <div className="grid grid-cols-2 gap-3">
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-br from-muted to-muted/50 h-24 flex items-center justify-center">
              <Package className="w-8 h-8 text-muted-foreground" />
            </div>
            <CardContent className="p-3">
              <div className="text-sm font-medium text-foreground mb-1">Casual</div>
              <Badge variant="secondary" className="text-xs">Novo</Badge>
            </CardContent>
          </Card>
          
          <Card className="overflow-hidden">
            <div className="bg-gradient-to-br from-gold/20 to-gold/30 h-24 flex items-center justify-center">
              <Gift className="w-8 h-8 text-gold-dark" />
            </div>
            <CardContent className="p-3">
              <div className="text-sm font-medium text-foreground mb-1">Presente</div>
              <Badge variant="secondary" className="text-xs">Novo</Badge>
            </CardContent>
          </Card>
        </div>
      </div> */}

      {/* Espaçamento para navegação mobile */}
      <div className="h-20 lg:h-0"></div>

      {/* Modal de Completar Cadastro */}
      <CompletarCadastro
        isOpen={showCompletarCadastro}
        userData={user}
        onSuccess={handleCompletarCadastroSuccess}
      />
    </div>
  );
};

export default Home;